import React, { Fragment, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import { array, bool, func, number, shape, string, object } from 'prop-types';
import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Button, Chip, Grid, withStyles } from '@material-ui/core';
import styles from './styles';
import ListContainer from '../../../list';
import { FormSelect, FormTextField } from '../../../../components';
import EditApiLocationDialog from "./EditApiLocationDialog";
import {
  getApiLocationList,
  getApiLocationListTotalElements,
  getApiLocationListTotalPages,
  getIsApiLocationDeleteError,
  getIsApiLocationListError,
  getIsApiLocationUpdateError,
  getIsLoading,
} from "../../../../reducers/api";
import { fetchApiLocations, updateApiLocation, deleteApiLocation } from "../../../../actions/api";
import {
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  API_LOCATION_TYPE,
  ALERT_ERROR,
  ALL_LOCATION,
  DEFAULT_LOCATION, ALERT_SUCCESS,
} from '../../../../constants';
import { hasAdminRole } from "../../../../utils";

export const ApiLocation = (props) => {
  const {
    classes,
    isLoading,
    totalPages,
    totalElements = 0,
    apiUuid,
    apiDetails,
    apiLocationList,
    fetchApiLocations,
    updateApiLocation,
    deleteApiLocation,
    isApiLocationListError,
    isApiLocationUpdateError,
    isApiLocationDeleteError,
    userContext,
  } = props;

  const intl = getI18n(useIntl());

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [filterByProxyName, setFilterByProxyName] = useState('');
  const [filterByApiLocationType, setFilterByApiLocationType] = useState(ALL_LOCATION);
  const [filterByApiLocationUrl, setFilterByApiLocationUrl] = useState('');
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [proxyUuidToUpdate, setProxyUuidToUpdate] = useState('');
  const [locationUrlToUpdate, setLocationUrlToUpdate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const basePageId = 'api-details-api-location'

  useEffect(() => fetchApiLocationsList({}), []);

  useEffect(() => {
    isApiLocationListError && notifyMessages(ALERT_ERROR, intl.getI18nMessage('error.api.location.tab.list'));
  }, [isApiLocationListError]);

  useEffect(() => {
    isApiLocationUpdateError && notifyMessages(ALERT_ERROR, intl.getI18nMessage('error.api.location.tab.update'));
  }, [isApiLocationUpdateError]);

  useEffect(() => {
    isApiLocationDeleteError && notifyMessages(ALERT_ERROR, intl.getI18nMessage('error.api.location.tab.restore'));
  }, [isApiLocationDeleteError]);

  const fetchApiLocationsList = (params) => {
    const { proxyName, apiLocationType, apiLocationUrl, page, size } = params
    fetchApiLocations({
      apiUuid,
      ...(filterByProxyName && { proxyName : proxyName || filterByProxyName }),
      ...(apiLocationType && apiLocationType !== ALL_LOCATION &&
        { isDefault : apiLocationType === DEFAULT_LOCATION }),
      ...(filterByApiLocationUrl && { apiLocation : apiLocationUrl || filterByApiLocationUrl }),
      page,
      size : size || rowsPerPage,
    });
  }

  const getColumns = () => [
    {
      id: 'proxyName',
      label: getI18nFormattedMessage('label.api.location.tab.list.column.proxy'),
      minWidth: 100,
      ...(hasAdminRole(userContext) && { link: '/publish/api-proxy/details' }),
    },
    {
      id: 'locationUrl',
      label: getI18nFormattedMessage('label.api.location.tab.list.column.location'),
      minWidth: 400,
      format : (value, secondaryValue, uuid) => formatLocationUrl(value, uuid),
    },
    {
      id: 'actions',
      label: getI18nFormattedMessage('label.api.location.tab.list.column.actions'),
      minWidth: 50,
      format: (value, secondaryValue, uuid) => renderActions(uuid),
    },
  ]

  const formatLocationUrl = (value, uuid) =>
    <Fragment>
      {value + " "}
      {isApiLocationDefault(uuid) && <Chip label={getI18nFormattedMessage('label.default')} size={"small"}/>}
    </Fragment>

  const renderActions = (uuid) => (
    <Fragment>
      <Button
        id="api-location-copy-button"
        data-apim-test="api-location-copy-button"
        className={classes.actionButton}
        onClick={() => handleCopy(uuid)} variant="text">
        {getI18nFormattedMessage('label.copy')}
      </Button>
      <Button
        id="api-location-edit-button"
        data-apim-test="api-location-edit-button"
        className={classes.actionButton}
        onClick={() => handleEdit(uuid)}
        variant="text">
        {getI18nFormattedMessage('label.edit')}
      </Button>
    </Fragment>
  )

  const getApiLocationByUuid = (uuid) => apiLocationList.find(x => x.proxyUuid === uuid)['locationUrl'];
  const isApiLocationDefault = (uuid) => apiLocationList.find(x => x.proxyUuid === uuid)['isDefault'];

  const handleCopy = (uuid) => navigator.clipboard.writeText(getApiLocationByUuid(uuid))
    .then(() => notifyMessages(ALERT_SUCCESS, intl.getI18nMessage("label.api.location.tab.copy.success"), 2500))

  const handleEdit = (uuid) => openAlertDialog(uuid);

  const notifyMessages = (status, message, timeout) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
    if(timeout) setTimeout(()=> {
      setNotificationStatus('');
      setNotificationMessage('');
    }, timeout)
  };

  const isFilterDirty = () =>
    filterByProxyName !== '' ||
    filterByApiLocationType !== ALL_LOCATION ||
    filterByApiLocationUrl !== '';

  /* Filter by Proxy name events*/
  const onFilterByProxyNameChange = (value) => setFilterByProxyName(value);
  const onFilterByProxyNameKeyPress = (e) => e.key === 'Enter' && fetchApiLocationsList({});
  const onFilterByProxyNameBlur = () => fetchApiLocationsList({});

  /* Filter by Api location url events*/
  const onFilterByApiLocationUrlChange = (value) => setFilterByApiLocationUrl(value);
  const onFilterByApiLocationUrlKeyPress = (e) => e.key === 'Enter' && fetchApiLocationsList({});
  const onFilterByApiLocationUrlBlur = () => fetchApiLocationsList({});

  /* Filter by Api location type events*/
  const onFilterByApiLocationTypeChange = (locationType) => {
    setFilterByApiLocationType(locationType);
    fetchApiLocationsList({ apiLocationType : locationType })
  };

  /* Handle pagination */
  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchApiLocationsList({ page: newPage });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  /* Handle rows visible in a page */
  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchApiLocationsList({ size: newRowsPerPage });
  };

  const noResultsMessage = isFilterDirty() ?
    intl.getI18nMessage('label.api.location.tab.list.filter.no.results') :
    intl.getI18nMessage('label.api.location.tab.list.no.results');

  /* Handle alert dialog events */
  const handleAlertDialogSave = async() => {
    if(locationUrlToUpdate === apiDetails.locationUrl) {
      await deleteApiLocation({ proxyUuid : proxyUuidToUpdate, apiUuid })
    } else {
      await updateApiLocation({ proxyUuid : proxyUuidToUpdate, apiUuid,
        locationUrl : locationUrlToUpdate })
    }
    fetchApiLocationsList({});
    closeAlertDialog();
  }
  const handleAlertDialogCancel = () => closeAlertDialog();
  const handleAlertDialogRestoreDefault = () => setLocationUrlToUpdate(apiDetails.locationUrl)
  const onLocationUrlChange = (url) => setLocationUrlToUpdate(url);

  const openAlertDialog = (uuid) => {
    setLocationUrlToUpdate(getApiLocationByUuid(uuid));
    setProxyUuidToUpdate(uuid);
    setIsAlertDialogOpen(true);
  }
  const closeAlertDialog = () => {
    setProxyUuidToUpdate('');
    setLocationUrlToUpdate('');
    setIsAlertDialogOpen(false);
  }

  return (
    <Fragment>
      <EditApiLocationDialog
        isAlertDialogOpen={isAlertDialogOpen}
        locationUrl={locationUrlToUpdate}
        onLocationUrlChange={onLocationUrlChange}
        onRestoreDefault={handleAlertDialogRestoreDefault}
        disableRestoreDefault={locationUrlToUpdate === apiDetails.locationUrl}
        onSave={handleAlertDialogSave}
        onCancel={handleAlertDialogCancel}
        classes={classes}
      />
      <ListContainer
        listPageId="api-details-api-location"
        isLoading={isLoading}
        notificationId="api-details-api-location-list-notification"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationStatus}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        hasNotificationIcon={true}
        pageFilterAndSortClass={classes.pageFilterAndSort}
        pageBodyClass={classes.listContainer}
        classes={classes}
        filterAndSortContent={
          <div className={classes.textFiltersContainer}>
            <Fragment>
              <Grid item md={3} sm={3} xs={12} className={classes.filterField}>
                <FormTextField
                  name={intl.getI18nMessage('label.filter')}
                  value={filterByProxyName}
                  placeholder={intl.getI18nMessage('label.api.location.tab.filter.proxy')}
                  handleChange={onFilterByProxyNameChange}
                  onKeyPress={onFilterByProxyNameKeyPress}
                  handleBlur={onFilterByProxyNameBlur}
                  id={`${basePageId}-filter-by-proxy-name`}
                  data-apim-test={`${basePageId}-filter-by-proxy-name`}
                />
              </Grid>
              <Grid item md={3} sm={3} xs={12} className={classes.filterField}>
                <FormSelect
                  {...props}
                  id={`${basePageId}-filter-by-api-location-type`}
                  data-apim-test={`${basePageId}-filter-by-api-location-type`}
                  name={intl.getI18nMessage('label.filter')}
                  value={filterByApiLocationType}
                  data={API_LOCATION_TYPE}
                  handleChange={onFilterByApiLocationTypeChange}
                  hideLabel
                  noNoneOption
                  noNativeSelect
                />
              </Grid>
              <Grid item md={3} sm={3} xs={12} className={classes.filterField}>
                <FormTextField
                  {...props}
                  id={`${basePageId}-filter-by-api-location-url`}
                  data-apim-test={`${basePageId}-filter-by-api-location-url`}
                  name={intl.getI18nMessage('label.filter')}
                  value={filterByApiLocationUrl}
                  textFieldClass={classes.textField}
                  placeholder={intl.getI18nMessage('label.api.location.tab.filter.location')}
                  handleChange={onFilterByApiLocationUrlChange}
                  onKeyPress={onFilterByApiLocationUrlKeyPress}
                  handleBlur={onFilterByApiLocationUrlBlur}
                  hideLabel
                />
              </Grid>
            </Fragment>
          </div>
        }
        columns={getColumns()}
        uuidName={"proxyUuid"}
        rows={apiLocationList}
        noResultsMessage={noResultsMessage}
        page={page}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
      />
    </Fragment>

  );
};

ApiLocation.propTypes = {
  classes: shape({ }),
  isLoading: bool,
  push: func,
  totalPages: number,
  totalElements: number,
  apiUuid: string,
  apiDetails : shape({}),
  apiLocationList : array,
  fetchApiLocations : func,
  updateApiLocation : func,
  deleteApiLocation : func,
  isApiLocationListError : bool,
  isApiLocationUpdateError : bool,
  isApiLocationDeleteError: bool,
  userContext: object,
};

const mapStateToProps = (state) => ({
  isLoading : getIsLoading(state),
  apiLocationList : getApiLocationList(state),
  isApiLocationListError : getIsApiLocationListError(state),
  isApiLocationUpdateError : getIsApiLocationUpdateError(state),
  isApiLocationDeleteError : getIsApiLocationDeleteError(state),
  totalPages: getApiLocationListTotalPages(state),
  totalElements: getApiLocationListTotalElements(state),
});

const mapDispatchToProps = {
  push,
  fetchApiLocations,
  updateApiLocation,
  deleteApiLocation,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ApiLocation);
